/**
 * The overview at the top of the cellar: what you have, in one look.
 *
 * Everything here is derived in the browser from the rows the grid is already
 * holding, for the reason the filtering is — a cellar is a couple of hundred
 * rows and they have all arrived. An aggregate endpoint would be a second
 * round trip that could disagree with the tiles underneath it after a tap on
 * the stepper, which is exactly the class of bug the local-first choice avoids.
 *
 * Distributions are counted by the bottle rather than by the label, because
 * six of one Rioja is not the same holding as six different ones and the
 * question this card answers is "what do I have". The exception is a cellar
 * that has been drunk dry: weighting by a column of noughts would render every
 * bar empty and say nothing, so it falls back to counting labels and says so.
 */
import type { CellarWine } from '../lib/types'

/**
 * The pours, in the order a wine list puts them, with a swatch each.
 *
 * The swatches are the colours in the glass rather than a categorical palette,
 * so the legend is mostly decoration: nobody has to look up which wedge is the
 * white. `unknown` is what `_wine_payload` writes when the label was illegible.
 */
const POURS = [
  { id: 'sparkling', swatch: '#f0c96b' },
  { id: 'white', swatch: '#e6dca6' },
  { id: 'orange', swatch: '#d98b3c' },
  { id: 'rosé', swatch: '#e79aa6' },
  { id: 'red', swatch: '#a52440' },
  { id: 'fortified', swatch: '#9c5a37' },
  { id: 'unknown', swatch: '#57534e' },
] as const

const SWATCHES: Record<string, string> = Object.fromEntries(
  POURS.map((pour) => [pour.id, pour.swatch]),
)

/** A four-digit number off a label is not always a vintage — an alcohol duty
 *  code and a lot number both look like one. Anything outside the window from
 *  here to a shade after today is counted as non-vintage rather than plotted,
 *  because one 1863 stretches the axis until the rest of the cellar is a line. */
const EARLIEST_PLAUSIBLE_VINTAGE = 1900

interface Tally {
  name: string
  bottles: number
}

interface Summary {
  /** True when the counts below are labels rather than bottles. */
  byLabel: boolean
  bottles: number
  labels: number
  emptied: number
  singletons: number
  producers: number
  regions: number
  colours: Tally[]
  grapes: Tally[]
  places: Tally[]
  vintages: { label: string; bottles: number; title: string }[]
  oldest: number | null
  newest: number | null
  nonVintage: number
  averageAge: number | null
  averageAbv: number | null
  rated: number
  averageRating: number | null
  deepest: { name: string; bottles: number } | null
  mostSeen: { name: string; times: number } | null
  since: Date | null
  latest: Date | null
}

export default function CellarSummary({ wines }: { wines: CellarWine[] }) {
  // Not memoised: this is a few hundred rows of arithmetic against a query
  // that only changes when the cellar does, and `useMemo` here would cost more
  // in ceremony than it saves in milliseconds.
  const summary = summarise(wines)
  if (!summary.labels) return null

  return (
    // Named rather than anonymous, so it is a landmark to skip to rather than
    // a wall of numbers to read past — and so a journey can scope an assertion
    // to it, since half the words in here also appear on the tiles below.
    <section className="card space-y-4 p-4" aria-labelledby="at-a-glance">
      <header className="flex items-baseline justify-between gap-3">
        <h3 id="at-a-glance" className="label">
          The cellar at a glance
        </h3>
        {summary.since && (
          <span className="text-[11px] text-stone-500">
            collecting since {month(summary.since)}
          </span>
        )}
      </header>

      {/* Four across even on a phone. Stacked two-by-two they are legible but
          they push the grid a whole screen down, and the headline numbers are
          the part that has to be above the fold. */}
      <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
        <Stat value={summary.bottles} unit={plural(summary.bottles, 'bottle')} />
        <Stat value={summary.labels} unit={plural(summary.labels, 'label')} />
        <Stat value={summary.producers} unit={plural(summary.producers, 'producer')} />
        <Stat value={summary.regions} unit={plural(summary.regions, 'region')} />
      </div>

      <Pours summary={summary} />

      <div className="grid gap-4 sm:grid-cols-2">
        <Bars
          heading={summary.byLabel ? 'Grapes, by label' : 'Grapes, by bottle'}
          tallies={summary.grapes}
          empty="No grape has been read off a label yet."
        />
        <Bars
          heading={summary.byLabel ? 'Regions, by label' : 'Regions, by bottle'}
          tallies={summary.places}
          empty="No region has been pinned down yet."
        />
      </div>

      <Vintages summary={summary} />

      <Nerdery summary={summary} />
    </section>
  )
}

/**
 * One headline number.
 *
 * The space between the two spans is deliberate and load-bearing: the unit is
 * a block, so it is invisible on screen, but it makes the tile read as "3
 * bottles" rather than "3bottles" to a screen reader and to the journeys.
 */
function Stat({ value, unit }: { value: number; unit: string }) {
  return (
    <p className="rounded-xl bg-black/25 px-2 py-2 sm:px-3">
      <span className="font-display text-xl leading-none text-stone-100 sm:text-2xl">
        {value}
      </span>{' '}
      <span className="mt-1 block truncate text-[10px] text-stone-500 sm:text-[11px]">
        {unit}
      </span>
    </p>
  )
}

/** The colour split, as one bar rather than six. */
function Pours({ summary }: { summary: Summary }) {
  const total = summary.colours.reduce((sum, colour) => sum + colour.bottles, 0)
  if (!total) return null

  return (
    <div className="space-y-2">
      <div className="flex h-2.5 gap-0.5 overflow-hidden rounded-full">
        {summary.colours.map((colour) => (
          <div
            key={colour.name}
            className="h-full first:rounded-l-full last:rounded-r-full"
            style={{
              width: `${(colour.bottles / total) * 100}%`,
              background: SWATCHES[colour.name] ?? SWATCHES.unknown,
            }}
            title={`${colour.name}: ${colour.bottles}`}
          />
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-3 gap-y-1">
        {summary.colours.map((colour) => (
          <li key={colour.name} className="flex items-center gap-1.5 text-[11px]">
            <span
              aria-hidden
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ background: SWATCHES[colour.name] ?? SWATCHES.unknown }}
            />
            <span className="text-stone-300">{colour.name}</span>
            <span className="text-stone-500">
              {colour.bottles} · {Math.round((colour.bottles / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** A ranked list with the bar behind the row rather than beside it, so the
 *  names stay left-aligned and readable at five to a column. */
function Bars({
  heading,
  tallies,
  empty,
}: {
  heading: string
  tallies: Tally[]
  empty: string
}) {
  const most = tallies[0]?.bottles ?? 0

  return (
    <div className="space-y-1.5">
      <h4 className="label">{heading}</h4>
      {!tallies.length && <p className="text-[11px] text-stone-600">{empty}</p>}
      <ol className="space-y-1">
        {tallies.map((tally) => (
          <li
            key={tally.name}
            className="relative flex items-center justify-between gap-2 overflow-hidden rounded-md px-2 py-1"
          >
            <span
              aria-hidden
              className="absolute inset-y-0 left-0 rounded-md bg-wine-800/45"
              style={{ width: `${(tally.bottles / most) * 100}%` }}
            />
            <span className="relative z-10 truncate text-xs text-stone-200">
              {tally.name}
            </span>
            <span className="relative z-10 shrink-0 text-[11px] tabular-nums text-stone-400">
              {tally.bottles}
            </span>
          </li>
        ))}
      </ol>
    </div>
  )
}

/**
 * The vintage spread, as a histogram with the empty years left in.
 *
 * Dropping the years you own nothing from is what turns a histogram back into
 * a bar chart: the gap between a 2015 and a 2021 is the interesting part of a
 * cellar's shape, and closing it up hides that there is one.
 */
function Vintages({ summary }: { summary: Summary }) {
  if (!summary.vintages.length) return null
  const most = Math.max(...summary.vintages.map((bucket) => bucket.bottles))

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="label">Vintages</h4>
        <span className="text-[11px] text-stone-500">
          {summary.oldest === summary.newest
            ? summary.oldest
            : `${summary.oldest}–${summary.newest}`}
          {summary.averageAge !== null && `, ${summary.averageAge} years old on average`}
          {summary.nonVintage > 0 && `, ${summary.nonVintage} NV`}
        </span>
      </div>
      <ol className="flex h-14 items-end gap-1">
        {summary.vintages.map((bucket) => (
          <li key={bucket.label} className="flex h-full flex-1 flex-col justify-end gap-1">
            <div
              className="rounded-t-sm bg-wine-600/80"
              style={{
                // A year you own one bottle from has to be a visible sliver
                // rather than nothing, or the axis lies about the spread.
                height: bucket.bottles ? `${Math.max(8, (bucket.bottles / most) * 100)}%` : 0,
              }}
              title={`${bucket.title}: ${bucket.bottles}`}
            />
            <span className="text-center text-[9px] text-stone-600">{bucket.label}</span>
          </li>
        ))}
      </ol>
    </div>
  )
}

/** The facts that do not fit a bar. */
function Nerdery({ summary }: { summary: Summary }) {
  const facts: [string, string][] = []
  if (summary.averageAbv !== null) {
    facts.push(['Average strength', `${summary.averageAbv.toFixed(1)}% abv`])
  }
  if (summary.deepest) {
    facts.push(['Deepest holding', `${summary.deepest.bottles} × ${summary.deepest.name}`])
  }
  if (summary.singletons) {
    facts.push(['Down to the last bottle', `${summary.singletons} of ${summary.labels}`])
  }
  if (summary.rated) {
    facts.push([
      'Rated',
      `${summary.rated} of ${summary.labels}${
        summary.averageRating !== null ? `, ★ ${summary.averageRating.toFixed(1)} average` : ''
      }`,
    ])
  }
  if (summary.mostSeen) {
    facts.push([
      'Most photographed',
      `${summary.mostSeen.name}, ${summary.mostSeen.times}×`,
    ])
  }
  if (summary.emptied) {
    facts.push(['Drunk dry', `${summary.emptied} of ${summary.labels}`])
  }
  if (summary.latest) {
    facts.push(['Last added', month(summary.latest)])
  }
  if (!facts.length) return null

  return (
    <dl className="grid gap-x-4 gap-y-1.5 border-t border-white/5 pt-3 sm:grid-cols-2">
      {facts.map(([term, detail]) => (
        <div key={term} className="flex items-baseline justify-between gap-2">
          <dt className="shrink-0 text-[11px] text-stone-500">{term}</dt>
          <dd className="truncate text-[11px] text-stone-300">{detail}</dd>
        </div>
      ))}
    </dl>
  )
}

// ---------------------------------------------------------------------------

/** Everything the card renders, in one pass over the rows. */
export function summarise(wines: CellarWine[]): Summary {
  const bottles = wines.reduce((sum, wine) => sum + (wine.bottleCount ?? 0), 0)
  // See the module docstring: a dry cellar is counted by the label so that the
  // bars say something rather than all reading nought.
  const byLabel = bottles === 0
  const weight = (wine: CellarWine) => (byLabel ? 1 : (wine.bottleCount ?? 0))

  const colours = new Map<string, number>()
  const grapes = new Map<string, number>()
  const places = new Map<string, number>()
  const producers = new Set<string>()
  const years = new Map<number, number>()
  let nonVintage = 0
  let abvBottles = 0
  let abvWeighted = 0
  let ratingSum = 0
  let rated = 0
  let emptied = 0
  let singletons = 0
  let deepest: Summary['deepest'] = null
  let mostSeen: Summary['mostSeen'] = null
  let since: Date | null = null
  let latest: Date | null = null

  for (const wine of wines) {
    const share = weight(wine)
    const name = label(wine)

    add(colours, pour(wine.colour), share)
    for (const grape of wine.grapes ?? []) add(grapes, tidy(grape), share)
    if (wine.region) add(places, tidy(wine.region), share)
    if (wine.producer) producers.add(tidy(wine.producer))

    const year = vintage(wine.vintage)
    if (year) add(years, year, share)
    else nonVintage += 1

    if (typeof wine.abv === 'number' && wine.abv > 0 && share > 0) {
      abvBottles += share
      abvWeighted += wine.abv * share
    }
    if (wine.myRating) {
      rated += 1
      ratingSum += wine.myRating
    }

    const count = wine.bottleCount ?? 0
    if (!count) emptied += 1
    if (count === 1) singletons += 1
    if (count > 1 && count > (deepest?.bottles ?? 1)) deepest = { name, bottles: count }

    const times = wine.sightingCount ?? 0
    if (times > 1 && times > (mostSeen?.times ?? 1)) mostSeen = { name, times }

    since = earlier(since, when(wine.firstSeenAt ?? wine.lastSeenAt))
    latest = later(latest, when(wine.lastSeenAt ?? wine.firstSeenAt))
  }

  const owned = [...years.keys()]
  const oldest = owned.length ? Math.min(...owned) : null
  const newest = owned.length ? Math.max(...owned) : null
  const dated = [...years.values()].reduce((sum, count) => sum + count, 0)

  return {
    byLabel,
    bottles,
    labels: wines.length,
    emptied,
    singletons,
    producers: producers.size,
    regions: places.size,
    // Wine-list order rather than biggest-first: the bar reads left to right
    // as lightest to heaviest, which is a shape you recognise at a glance and
    // which does not rearrange itself every time you drink something.
    colours: POURS.map((pour) => ({ name: pour.id, bottles: colours.get(pour.id) ?? 0 }))
      .filter((colour) => colour.bottles > 0),
    grapes: rank(grapes, 5),
    places: rank(places, 5),
    vintages: buckets(years, oldest, newest),
    oldest,
    newest,
    nonVintage,
    averageAge:
      dated > 0
        ? Math.round(
            [...years].reduce(
              (sum, [year, count]) => sum + (new Date().getFullYear() - year) * count,
              0,
            ) / dated,
          )
        : null,
    averageAbv: abvBottles > 0 ? abvWeighted / abvBottles : null,
    rated,
    averageRating: rated > 0 ? ratingSum / rated : null,
    deepest,
    mostSeen,
    since,
    latest,
  }
}

/**
 * The vintage axis: one bar a year while that fits, a bar a decade when it
 * does not, and the years in between kept even when they are empty.
 */
function buckets(
  years: Map<number, number>,
  oldest: number | null,
  newest: number | null,
): Summary['vintages'] {
  if (oldest === null || newest === null) return []
  const span = newest - oldest + 1
  if (span <= 12) {
    return Array.from({ length: span }, (_, offset) => {
      const year = oldest + offset
      return {
        // Two digits, because eight of them have to fit across a phone. The
        // apostrophe is what stops '18' reading as a count rather than a year.
        label: `’${String(year).slice(2)}`,
        bottles: years.get(year) ?? 0,
        title: String(year),
      }
    })
  }

  const first = Math.floor(oldest / 10) * 10
  const decades = Math.floor(newest / 10) - Math.floor(oldest / 10) + 1
  return Array.from({ length: decades }, (_, offset) => {
    const decade = first + offset * 10
    let bottles = 0
    for (const [year, count] of years) {
      if (year >= decade && year < decade + 10) bottles += count
    }
    return {
      label: `’${String(decade).slice(2)}`,
      bottles,
      title: `${decade}s`,
    }
  })
}

function add<K>(counts: Map<K, number>, key: K, share: number): void {
  counts.set(key, (counts.get(key) ?? 0) + share)
}

/** Biggest first, then alphabetically, so a tie does not reshuffle on rerender. */
function rank(counts: Map<string, number>, top: number): Tally[] {
  return [...counts]
    .filter(([, bottles]) => bottles > 0)
    .sort(([leftName, left], [rightName, right]) =>
      right - left || leftName.localeCompare(rightName),
    )
    .slice(0, top)
    .map(([name, bottles]) => ({ name, bottles }))
}

function pour(colour: string | undefined): string {
  const value = tidy(colour ?? '').toLowerCase()
  if (value === 'rose') return 'rosé'
  return POURS.some((known) => known.id === value) ? value : 'unknown'
}

function vintage(value: CellarWine['vintage']): number | null {
  const year = Number.parseInt(String(value ?? ''), 10)
  if (!Number.isFinite(year)) return null
  return year >= EARLIEST_PLAUSIBLE_VINTAGE && year <= new Date().getFullYear() + 2
    ? year
    : null
}

function label(wine: CellarWine): string {
  return (
    wine.displayName ||
    [wine.producer, wine.cuvee, wine.vintage].filter(Boolean).join(' ') ||
    'a wine'
  )
}

function tidy(value: string): string {
  return value.trim()
}

function when(value: string | undefined): Date | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function earlier(left: Date | null, right: Date | null): Date | null {
  if (!left || !right) return left ?? right
  return right < left ? right : left
}

function later(left: Date | null, right: Date | null): Date | null {
  if (!left || !right) return left ?? right
  return right > left ? right : left
}

function month(date: Date): string {
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

function plural(count: number, noun: string): string {
  return count === 1 ? noun : `${noun}s`
}
